'use client';

import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '@/contexts/AuthContext';

const MAX = 20;

function parseNumbers(text, totalTickets) {
  const nums = String(text)
    .split(/[^0-9]+/)
    .map((s) => parseInt(s, 10))
    .filter((n) => Number.isInteger(n) && n > 0 && (!totalTickets || n <= 1000000));
  return [...new Set(nums)].slice(0, MAX);
}

/**
 * Saved lucky numbers above the number grid: see which are free in this
 * competition, pick the free ones in one tap, save your current picks, or
 * edit the list. Signed-in players only.
 */
export default function LuckyNumbers({ totalTickets, takenNumbers, myNumbers, onPick }) {
  const { isAuthenticated, userProfile, updateProfile } = useAuth();
  const saved = useMemo(
    () => (Array.isArray(userProfile?.lucky_numbers) ? userProfile.lucky_numbers : []),
    [userProfile]
  );
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [message, setMessage] = useState('');

  if (!isAuthenticated || !userProfile) return null;

  const taken = new Set(takenNumbers);
  const mine = new Set(myNumbers);
  const status = (n) => (n > totalTickets ? 'out' : mine.has(n) ? 'mine' : taken.has(n) ? 'taken' : 'free');
  const free = saved.filter((n) => status(n) === 'free');

  const save = async (numbers) => {
    const { error } = await updateProfile({ lucky_numbers: numbers });
    setMessage(error ? 'Couldn’t save your lucky numbers. Please try again.' : 'Lucky numbers saved.');
    if (!error) setEditing(false);
  };

  const chip = {
    free: 'border-accent text-foreground',
    mine: 'border-accent bg-accent text-accent-foreground',
    taken: 'border-white/15 text-muted-foreground line-through',
    out: 'border-white/10 text-muted-foreground/60',
  };

  return (
    <div className="mb-5 rounded-[6px] border border-border bg-muted/40 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-semibold text-foreground">Your lucky numbers</p>
        <button
          type="button"
          onClick={() => {
            setDraft(saved.join(', '));
            setEditing((v) => !v);
            setMessage('');
          }}
          className="text-sm font-semibold text-accent underline underline-offset-4"
        >
          {editing ? 'Cancel' : saved.length ? 'Edit' : 'Add some'}
        </button>
      </div>

      {editing ? (
        <div className="mt-3">
          <label htmlFor="lucky-input" className="text-sm text-muted-foreground">
            Up to {MAX} numbers, separated by commas.
          </label>
          <div className="mt-2 flex gap-2">
            <input
              id="lucky-input"
              inputMode="numeric"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="min-w-0 flex-1 rounded-[6px] border border-border bg-input px-3 py-2 text-foreground"
              placeholder="7, 13, 21"
            />
            <button
              type="button"
              onClick={() => save(parseNumbers(draft, totalTickets))}
              className="rounded-[6px] bg-accent px-4 py-2 font-semibold text-accent-foreground"
            >
              Save
            </button>
          </div>
        </div>
      ) : saved.length ? (
        <>
          <ul className="mt-3 flex flex-wrap gap-2" aria-label="Your lucky numbers and whether they're free">
            {saved.map((n) => (
              <li
                key={n}
                className={`min-w-[2.6rem] rounded-[6px] border px-2 py-1 text-center text-sm font-bold tabular-nums ${chip[status(n)]}`}
                title={{ free: 'Free', mine: 'Yours', taken: 'Taken', out: 'Not in this competition' }[status(n)]}
              >
                {n}
              </li>
            ))}
          </ul>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
            {free.length > 0 ? (
              <button
                type="button"
                onClick={() => onPick(free)}
                className="rounded-[6px] bg-accent px-4 py-2 text-sm font-semibold text-accent-foreground"
              >
                Pick my {free.length} free lucky number{free.length === 1 ? '' : 's'}
              </button>
            ) : (
              <span className="text-sm text-muted-foreground">None of your lucky numbers are free here.</span>
            )}
          </div>
        </>
      ) : (
        <p className="mt-2 text-sm text-muted-foreground">
          Save your favourite numbers and pick them in one tap in any competition.
        </p>
      )}

      {!editing && myNumbers.length > 0 && (
        <button
          type="button"
          onClick={() => save([...new Set([...myNumbers])].slice(0, MAX))}
          className="mt-3 block text-sm font-semibold text-accent underline underline-offset-4"
        >
          Save my current picks as my lucky numbers
        </button>
      )}
      {message && (
        <p className="mt-2 text-sm text-muted-foreground" role="status">
          {message}
        </p>
      )}
    </div>
  );
}

LuckyNumbers.propTypes = {
  totalTickets: PropTypes.number.isRequired,
  takenNumbers: PropTypes.array.isRequired,
  myNumbers: PropTypes.array.isRequired,
  onPick: PropTypes.func.isRequired,
};
